import { memo, useCallback, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { Avatar, ComboBox } from 'components';
import { ClickAwayListener } from '@mui/material';
import { PencilSimple } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { toast } from 'react-toastify';
import { AccessControl } from 'features/accessControl';
import { TableTooltip } from '../../TableTooltip';
import { DotsMenuButton } from '../../../components/DotsMenuButton';
import type { ComboBoxOption } from 'components/inputs/types';
import type { EditableDropdownCellProps } from './types';
import { useUser } from 'features/auth';
import { useEscapeToClose, useRestoreFocusOnStay } from '../../../hooks';
import {
  useEditableContextOptional,
  useShouldSkipInlineCommit,
} from '../../OptimizedTableProvider';
import { clearEditOnSuccess } from './inlineEditUtils';
import { prepareInlineCellHandoff, readActiveEditableSlot } from './commitActiveEditor';
import { INLINE_POPPER_OPTIONS } from './inlinePopperModifiers';
import { ClearFieldOption } from '../bulk/ClearFieldOption';

const CLEAR_OPTION_ID = '__clear__';

const DROPDOWN_POPPER_PROPS = {
  placement: 'bottom-start' as const,
  style: { maxWidth: 'calc(100vw - 16px)' },
  popperOptions: INLINE_POPPER_OPTIONS,
};

/**
 * Resolve API vars if they are a function (for context-dependent lookups)
 * This allows features to pass function-based vars in the registry that get
 * resolved with row context at render time (e.g., supplier contact persons)
 */
const resolveApiVars = (
  api: { vars?: string[] | ((ctx?: unknown) => string[]) },
  context: { row: { original: unknown } },
  userData: unknown
): string[] => {
  if (!api.vars) return [];
  if (typeof api.vars === 'function') {
    return api.vars({ userData, rowData: context.row.original });
  }
  return api.vars;
};

/**
 * EditableDropdownCell - A self-contained inline-editable dropdown cell for tables
 *
 * Provides:
 * - Click-to-edit functionality
 * - Searchable dropdown with API integration
 * - Avatar + label display in read mode
 * - No dependencies on legacy table components
 *
 * @example
 * ```tsx
 * <EditableDropdownCell
 *   context={cellContext}
 *   api={{
 *     url: apiBrands.get.search,
 *     key: 'brands',
 *     search: true,
 *     responseTransform: (res) => res.data.map(b => ({ id: b.id, label: b.name }))
 *   }}
 *   value={row.brand?.name}
 *   onChange={(e, value) => updateData(rowIndex, { brandId: value.id })}
 *   editableRowIndex={editableRowIndex}
 *   editableColumnId={editableColumnId}
 * />
 * ```
 */
const EditableDropdownCellInner = <T extends { id: number | string }>({
  context,
  api,
  options: staticOptions,
  value,
  onChange,
  tooltipTitle,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  groupBy = false,
  avatarColor,
  showAvatar = true,
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
  error: errorProp,
  disabled: disabledProp,
  required = true,
  requiredMessage,
  filterOptions,
}: EditableDropdownCellProps<T>) => {
  const { data: userData } = useUser();
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClickAway =
    onClickAwayProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  // Skip the click-away close while a navigation is being confirmed (the guard raises the flag
  // in a capture-phase pointerdown before the nav click), so the dropdown stays open and the
  // dialog can appear; "Leave" then closes it, "Stay" keeps it open (DL-60097).
  const shouldSkipCommit = useShouldSkipInlineCommit();

  const wrappedOnChange = useCallback(
    (
      e: React.SyntheticEvent,
      selectedValue: ComboBoxOption | ComboBoxOption[] | null | string,
      reason: string
    ) => {
      // The "Set empty" entry surfaces as a synthetic option with id `__clear__`.
      // Translate it to `null` so callers handle it uniformly with other clear paths.
      const isClearSelection =
        selectedValue !== null &&
        typeof selectedValue === 'object' &&
        !Array.isArray(selectedValue) &&
        selectedValue.id === CLEAR_OPTION_ID;

      // reason='clear' fires when user presses Delete/Backspace on empty input —
      // stay in edit mode instead of exiting as if saving (dl-57950)
      if (reason === 'clear' && !isClearSelection) {
        // A required field cannot be emptied: surface the mandatory-for-export
        // message as an error toast rather than an inline tooltip (dl-60826).
        if (required && value && requiredMessage) toast.error(requiredMessage);
        return;
      }

      const result = onChange?.(e, isClearSelection ? null : selectedValue, reason);
      clearEditOnSuccess(result ?? undefined, () =>
        setEditableState({ rowIndex: null, columnId: null })
      );
    },
    [onChange, setEditableState, required, value, requiredMessage]
  );

  const { i18n } = useLingui();
  const clearOption: ComboBoxOption = useMemo(
    () => ({
      id: CLEAR_OPTION_ID,
      label: t`Set empty`,
      CustomElement: ClearFieldOption,
    }),
    // Depend on i18n.locale so t`Set empty` is re-evaluated on locale change,
    // keeping ComboBox filter-matching in sync with the visible (Trans-driven) label.
    [i18n.locale]
  );
  const shouldShowClear = !required && !!value;
  const appendClearOption = useCallback(
    (opts: ComboBoxOption[]): ComboBoxOption[] => (shouldShowClear ? [...opts, clearOption] : opts),
    [shouldShowClear, clearOption]
  );

  // Resolve vars if they are a function (minimal change to support dynamic vars)
  const resolvedVars = api ? resolveApiVars(api, context, userData) : [];

  // Activate on mousedown rather than click. When another cell is mid-edit, that
  // cell commits on blur (which fires during *this* mousedown's focus change),
  // and its optimistic save rebuilds the table columns and remounts every cell
  // between mousedown and mouseup — so the click never fires here and the
  // dropdown would need a second click (DL-60264). mousedown runs before that
  // remount; the microtask defer lets the outgoing cell's blur/click-away reset
  // run first so it doesn't clobber our activation.
  const handleActivate = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (!isEditing && !disabledProp && context.row.original.id !== -1) {
        prepareInlineCellHandoff(
          () => readActiveEditableSlot(editableCtx),
          () => setEditableState({ rowIndex: null, columnId: null }),
          context.row.index,
          context.column.id
        );
        Promise.resolve().then(() => onCellEdit?.(context));
      }
    },
    [isEditing, disabledProp, context, onCellEdit, editableCtx, setEditableState]
  );

  const handleClickAway = useCallback(() => {
    // A navigation is being confirmed (nav-link click / leave dialog) — keep the dropdown open
    // so the guard can prompt and "Stay" preserves it (DL-60097).
    if (shouldSkipCommit()) return;
    // Clear edit state when clicking away without selecting
    onClickAway?.();
  }, [shouldSkipCommit, onClickAway]);

  useEscapeToClose(isEditing, handleClickAway);

  // After the leave-confirmation dialog closes via "Stay", MUI returns focus to the nav link
  // (not our still-open ComboBox popper). Pull it back so the user can keep navigating options
  // without re-clicking the cell (DL-60097).
  const editorRef = useRef<HTMLDivElement>(null);
  useRestoreFocusOnStay(editorRef, isEditing, 'input');

  const hasError = errorProp ?? false;
  const isAnyCellEditing = editableCtx?.editableState?.rowIndex != null;
  const isInteractive = !disabledProp && !isEditing;
  const showDotsMenu = isInteractive && context.row.original.id !== -1;

  // Memoize read mode content to prevent unnecessary re-renders
  const readModeContent = useMemo(() => {
    if (!value) {
      return <PlaceholderText>_</PlaceholderText>;
    }
    return showAvatar ? <AvatarLabel label={value} color={avatarColor} /> : <Label>{value}</Label>;
  }, [showAvatar, value, avatarColor]);

  const menuOptions = useMemo(
    () => [
      {
        label: t`Edit`,
        icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
        // Defer the activation by one microtask so any document-level
        // click listeners (notably the outgoing cell's ClickAwayListener,
        // which fires *after* React's synthetic handlers) get to run and
        // reset editableState to null first — otherwise that reset would
        // arrive after our setEditableState(this cell) in the same React
        // batch and clobber it, leaving the prior cell visually stuck in
        // edit mode and this cell not activated.
        onClick: () => {
          Promise.resolve().then(() => onCellEdit?.(context));
        },
      },
    ],
    [onCellEdit, context]
  );

  // Memoize the response transform function (only used when api is provided)
  const responseTransform = useCallback(
    (data: unknown) => {
      const transformed = api
        ? api.responseTransform(data).map(item => ({
            ...item,
            CustomElement: showAvatar ? AvatarLabel : undefined,
            // Merge original customProps (e.g. brand access data) with avatar display props
            customProps: showAvatar
              ? { ...(item.customProps ?? {}), label: item.label, color: item.avatarColor }
              : item.customProps,
          }))
        : [];
      const filtered = filterOptions ? filterOptions(transformed) : transformed;
      return appendClearOption(filtered);
    },
    [api, showAvatar, appendClearOption, filterOptions]
  );

  // Memoize the API config object — undefined when caller supplies static options instead
  const apiConfig = useMemo(
    () =>
      api
        ? {
            ...api,
            vars: resolvedVars,
            responseTransform,
          }
        : undefined,
    [api, resolvedVars, responseTransform]
  );

  const readInner = <ReadInner>{readModeContent}</ReadInner>;
  const tooltipValue = tooltipTitle || value || '';

  // Read-only fallback for users without write access: the read view with no
  // hover affordance, click-to-edit, or dots menu.
  const readFallback = (
    <ReadWrapper $editable={false} $disabled={!!disabledProp} $hasError={hasError}>
      {readInner}
    </ReadWrapper>
  );

  if (isEditing) {
    return (
      <AccessControl write fallback={readFallback}>
        <TooltipAnchor>
          <ClickAwayListener onClickAway={handleClickAway}>
            <EditorContainer ref={editorRef}>
              <ComboBox
                id={context.column.id}
                noOptionsText={t`No options`}
                api={apiConfig}
                options={apiConfig ? undefined : appendClearOption(staticOptions ?? [])}
                allowEmptySearchFetch
                open
                freeSolo={false}
                groupBy={groupBy}
                value={value}
                onChange={wrappedOnChange}
                componentsProps={{ popper: DROPDOWN_POPPER_PROPS }}
              />
            </EditorContainer>
          </ClickAwayListener>
        </TooltipAnchor>
      </AccessControl>
    );
  }

  return (
    <AccessControl write fallback={readFallback}>
      <ReadWrapper
        $editable={isInteractive}
        $disabled={!!disabledProp}
        $hasError={hasError}
        onMouseDown={handleActivate}
      >
        {isAnyCellEditing ? (
          readInner
        ) : (
          <TableTooltip title={tooltipValue}>{readInner}</TableTooltip>
        )}
      </ReadWrapper>
      {showDotsMenu && (
        <DotsMenuButtonWrapper>
          <DotsMenuButton menuOptions={menuOptions} />
        </DotsMenuButtonWrapper>
      )}
    </AccessControl>
  );
};

// Wrap with memo for performance optimization
export const EditableDropdownCell = memo(EditableDropdownCellInner) as <
  T extends { id: number | string },
>(
  props: EditableDropdownCellProps<T>
) => JSX.Element;

/**
 * AvatarLabel - Display component for dropdown items and read mode
 * Shows an avatar with initials and a label
 */
export const AvatarLabel = ({ label, color }: { label: string; color?: string }) => {
  if (!label) return null;
  // No explicit spacer here: every container that renders AvatarLabel (the cell
  // ReadInner/ReadOnlyContainer and the dropdown option row) applies a flex
  // `gap` of spacing/xs (8px), which is the avatar→label gap per Figma. A spacer
  // element would stack on top of that gap and widen it.
  return (
    <>
      <Avatar letters={label || ''} size="xs" color={color} />
      <Label>{label}</Label>
    </>
  );
};

/**
 * ReadOnlyDropdownCell - A read-only version of the dropdown cell
 * Has the same layout/styling as EditableDropdownCell but without click interaction
 */
export const ReadOnlyDropdownCell = ({
  value,
  showAvatar = true,
  avatarColor,
}: {
  value: string;
  showAvatar?: boolean;
  avatarColor?: string;
}) => {
  const content = showAvatar ? (
    <AvatarLabel label={value || ''} color={avatarColor} />
  ) : (
    <Label>{value || ''}</Label>
  );

  return (
    <TableTooltip title={value || ''}>
      <ReadOnlyContainer>{content}</ReadOnlyContainer>
    </TableTooltip>
  );
};

const ReadOnlyContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  width: 100%;
  padding: 0 ${p => p.theme.s.sm};
  gap: 8px;
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.75rem;
  line-height: 1rem;
`;

export const PlaceholderText = styled.span`
  color: ${p => p.theme.palette.text.placeholder ?? '#96999d'};
  font-style: normal;
  font-size: 0.75rem;
  line-height: 1rem;
`;

export const ReadWrapper = styled.div<{
  $editable: boolean;
  $disabled: boolean;
  $hasError: boolean;
}>`
  display: flex;
  align-items: center;
  align-self: stretch;
  flex: 1;
  min-width: 0;
  margin: -${p => p.theme.s.xs} 0;
  padding: ${p => p.theme.s.xs} ${p => p.theme.s.sm};
  gap: 8px;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  overflow: hidden;
  transition: background-color 0.15s;
  &:hover {
    background-color: ${p =>
      p.$editable && !p.$disabled ? p.theme.palette.utility.grey[100] : 'transparent'};
  }
  ${p => (p.$disabled ? 'opacity: 0.5;' : '')}
  ${p => (p.$hasError ? `box-shadow: inset 0 0 0 1px ${p.theme.palette.border.errorSolid};` : '')}
`;

export const ReadInner = styled.div<{ width?: number }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  gap: 8px;
  ${p => (p.width ? `width:${p.width}px` : '')}
`;

export const DotsMenuButtonWrapper = styled.div`
  max-width: 18px;
  margin-right: 4px;
`;

export const TooltipAnchor = styled.div`
  width: 100%;
`;

const EditorContainer = styled.div`
  width: 100%;

  .MuiAutocomplete-popper {
    min-width: min(300px, calc(100vw - 16px));
    max-width: calc(100vw - 16px);
  }
  .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
  && .MuiInput-root {
    padding-right: 0;
  }
`;
